/**
 * Orders temp-log gate — rendered by the kitchen orders surface INSTEAD
 * of order data while the chef has OVERDUE temperature logs (delivery
 * days before the kitchen's today with open CHEF_SUBMIT_TEMP_LOG
 * tasks — see `/api/kitchen-orders/shipments` `tempLogGate`).
 *
 * Mirrors the cafeteria Today dashboard's anatomy byte-for-byte: the
 * same `max-w-2xl` left-aligned column, the same warning `Alert` strip
 * (Today's "N past service days need completion"), and the same
 * StageCard row shape (title + chip left, action right — see
 * `components/production/day-record-card.tsx`). One card per overdue delivery
 * day, deep-linking into the temperature-log workflow FOR THAT DAY. The
 * server withholds the order data while gated, so completing the logs
 * refetches and unlocks.
 */

import { Alert, AlertDescription, AlertTitle } from '@ordo/ui/alert'
import { Badge } from '@ordo/ui/badge'
import { Button } from '@ordo/ui/button'
import { Card } from '@ordo/ui/card'
import { AlertTriangle } from 'lucide-react'
import { Link } from 'react-router'

import type { TempLogGateDay } from '@/api/kitchen-orders'
import { tempLogTaskIdForDay } from '@/lib/chefs/temp-log-days'

const DAY_LABEL = new Intl.DateTimeFormat('en-US', {
  weekday: 'long',
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
})

/** `YYYY-MM-DD` → "Wednesday, Aug 5" without local-timezone day drift. */
function gateDayLabel(dayKey: string): string {
  const parsed = new Date(`${dayKey}T00:00:00Z`)
  return Number.isNaN(parsed.getTime()) ? dayKey : DAY_LABEL.format(parsed)
}

export function TempLogGate({ days }: { days: TempLogGateDay[] }) {
  return (
    <div className="mx-auto mt-5 flex w-full max-w-2xl flex-col gap-4 px-4 pb-10 lg:px-0">
      <Alert variant="warning">
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
          <div className="flex-1">
            <AlertTitle>Complete pending temperature logs to see your orders</AlertTitle>
            <AlertDescription>
              {days.length === 1
                ? 'A past delivery day needs its temperature log.'
                : `${days.length} past delivery days need their temperature logs.`}{' '}
              Inform temperatures and sign to complete USDA production records.
            </AlertDescription>
          </div>
        </div>
      </Alert>
      {days.map((day) => (
        <Card key={day.dayKey} className="flex items-start justify-between gap-4 p-5">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold tracking-normal">{gateDayLabel(day.dayKey)}</h2>
              <Badge variant="warning" className="whitespace-nowrap text-xs">
                Required
              </Badge>
            </div>
            <p className="mt-1 text-sm text-muted-foreground">
              Log each package&apos;s kitchen and drop-off temperatures for this delivery day.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {day.firstOpenTaskId ? (
              <Button asChild>
                <Link to={`/tasks/${tempLogTaskIdForDay(day.dayKey)}/temperature-log`}>Log temperatures</Link>
              </Button>
            ) : (
              <Button asChild variant="outline">
                <Link to="/tasks">Open tasks</Link>
              </Button>
            )}
          </div>
        </Card>
      ))}
    </div>
  )
}
